const PAGE_COUNT_TIMEOUT_MS = 10_000;

export const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/**
 * Counts a PDF's pages in the browser so an over-long document is refused
 * before its bytes are uploaded. Returns null whenever the count is not
 * certain (not a PDF, unreadable, encrypted, slow): the server still enforces
 * the limit, so a failed count must never block an upload.
 */
export const countPdfPages = async (file: File): Promise<number | null> => {
  if (!isPdfFile(file)) return null;

  let destroy: (() => Promise<void>) | undefined;
  let timer: number | undefined;
  try {
    // Loaded on demand: pdf.js is large and only a PDF attachment needs it.
    const pdfjs = await import('pdfjs-dist');

    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url
    ).toString();

    const task = pdfjs.getDocument({
      data: new Uint8Array(await file.arrayBuffer()),
    });

    destroy = () => task.destroy();

    const timeout = new Promise<null>((resolve) => {
      timer = window.setTimeout(() => resolve(null), PAGE_COUNT_TIMEOUT_MS);
    });

    const document = await Promise.race([task.promise, timeout]);
    return document && Number.isInteger(document.numPages)
      ? document.numPages
      : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
    // Destroying the task also frees the loaded document and its worker.
    void destroy?.().catch(() => undefined);
  }
};
